import { authService } from '@/api/services/authService.ts';
import { RateLimiterMiddleware, RateLimitPresets } from '@/lib/rateLimiter';
import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';

const getBaseURL = () => import.meta.env.VITE_API_URL || '';

const PUBLIC_ENDPOINTS = [
  '/api/auth/login',
  '/api/auth/google',
  '/api/auth/magic-link',
  '/api/auth/verify-magic-link',
  '/api/health',
  '/api/metrics',
  '/api/metrics/jmx',
  '/api/openapi.yaml',
] as const;

function normalizePath(url: string): string {
  try {
    if (url.startsWith('http')) {
      return new URL(url).pathname;
    }
    return url.split('?')[0] ?? url;
  } catch {
    return url.split('?')[0] ?? url;
  }
}

const isPublicEndpoint = (url: string): boolean => {
  const path = normalizePath(url);
  return PUBLIC_ENDPOINTS.some((endpoint) => path === endpoint || path.endsWith(endpoint));
};

const axiosInstance = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

const rateLimiter = new RateLimiterMiddleware(RateLimitPresets.LENIENT);

axiosInstance.interceptors.request.use(rateLimiter.createRequestInterceptor(), (error) =>
  Promise.reject(error)
);

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = authService.getToken();
  const requestUrl = config.url || '';
  const isPublic = isPublicEndpoint(requestUrl);

  if (!isPublic && !token) {
    return Promise.reject(new Error('Authentication required. No token found.'));
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  config.headers['X-Request-ID'] = crypto.randomUUID();

  if (config.method === 'get' || config.method === 'GET') {
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';
  }

  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  config.metadata = { startTime: Date.now() };

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || '';
    if (error.response?.status === 401 && authService.getToken() && !isPublicEndpoint(requestUrl)) {
      authService.removeToken();
      authService.removeUserInfo();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const customInstance = <T>(config: AxiosRequestConfig) => {
  return axiosInstance.request<T>(config).then((response) => response.data);
};

export const apiClient = axiosInstance;
